/**
 * Cookie Consent Banner
 * GDPR-compliant cookie consent UI
 */

import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@/hooks/use-cookie-consent';
import { updatePostHogConsent } from '@/lib/posthog';

export function CookieConsent() {
  const { isPending, isLoaded, acceptAll: baseAcceptAll, acceptEssentialOnly: baseAcceptEssentialOnly } = useCookieConsent();

  // Wrap handlers to also update PostHog consent
  const acceptAll = useCallback(() => {
    baseAcceptAll();
    updatePostHogConsent();
  }, [baseAcceptAll]);

  const acceptEssentialOnly = useCallback(() => {
    baseAcceptEssentialOnly();
    updatePostHogConsent();
  }, [baseAcceptEssentialOnly]);

  // Don't render until we've loaded the saved preferences
  if (!isLoaded) return null;

  // Don't show if user has already made a choice
  if (!isPending) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom-5 duration-300">
      <div className="mx-auto max-w-4xl">
        <div className="rounded-xl border bg-card p-4 shadow-lg md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            {/* Content */}
            <div className="flex gap-3">
              <div className="hidden shrink-0 sm:block">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <Cookie className="h-5 w-5 text-primary" />
                </div>
              </div>
              <div className="space-y-1">
                <h3 className="font-semibold text-foreground">We value your privacy</h3>
                <p className="text-sm text-muted-foreground">
                  We use cookies to enhance your browsing experience, analyze site traffic, and personalize content.
                  By clicking "Accept All", you consent to our use of cookies.{' '}
                  <Link to="/cookies" className="text-primary hover:underline">
                    Learn more
                  </Link>
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row md:ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={acceptEssentialOnly}
                className="whitespace-nowrap"
              >
                Essential Only
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="whitespace-nowrap"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Minimal cookie consent banner variant
 * Use this for a less intrusive experience
 */
export function CookieConsentMinimal() {
  const { isPending, isLoaded, acceptAll: baseAcceptAll, acceptEssentialOnly: baseAcceptEssentialOnly } = useCookieConsent();

  const acceptAll = useCallback(() => {
    baseAcceptAll();
    updatePostHogConsent();
  }, [baseAcceptAll]);

  const acceptEssentialOnly = useCallback(() => {
    baseAcceptEssentialOnly();
    updatePostHogConsent();
  }, [baseAcceptEssentialOnly]);

  if (!isLoaded || !isPending) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:max-w-md animate-in slide-in-from-bottom-5 duration-300">
      <div className="rounded-lg border bg-card p-4 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            We use cookies to improve your experience.{' '}
            <Link to="/cookies" className="text-primary hover:underline">
              Learn more
            </Link>
          </p>
          <button
            onClick={acceptEssentialOnly}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={acceptEssentialOnly}
            className="flex-1"
          >
            Decline
          </Button>
          <Button
            size="sm"
            onClick={acceptAll}
            className="flex-1"
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
